<template>
  <div id="payment-callback" class="callback-page">
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card class="text-center callback-card">
          <div class="py-12">
            <!-- Loading State -->
            <div v-if="status === 'initializing' || status === 'processing'" class="processing-state">
              <div class="loading-icon mb-6">
                <vs-loading type="border" size="large"></vs-loading>
              </div>
              <h1 class="text-3xl font-bold text-gray-700 mb-4">
                {{ $t('FinalizingYourOrder') }}
              </h1>
              <p class="text-lg text-gray-600 mb-6">
                {{ $t('PleaseWaitWhileWeProcess') }}
              </p>
            </div>

            <!-- Success State -->
            <div v-else-if="status === 'success'" class="success-state">
              <div class="success-icon mb-6">
                <div class="checkmark-container">
                  <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                    <circle class="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                    <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                  </svg>
                </div>
              </div>
              
              <h1 class="text-3xl font-bold text-success mb-4 success-title">
                {{ $t('PaymentSuccessful') }}
              </h1>
              
              <p class="text-lg text-gray-600 mb-6 success-description">
                {{ $t('LicenseActivatedSuccessfully') }}
              </p>
              
              <!-- License Info -->
              <div v-if="licenseData" class="license-info mb-6">
                <div class="bg-gray-50 p-4 rounded-lg license-details">
                  <h3 class="font-semibold mb-2 license-title">{{ $t('LicenseDetails') }}</h3>
                  <p class="text-sm text-gray-600">
                    <strong>{{ $t('Edition') }}:</strong> {{ licenseData.Edition }}
                  </p>
                  <p class="text-sm text-gray-600">
                    <strong>{{ $t('Hostname') }}:</strong> {{ licenseData.Hostname }}
                  </p>
                  <p v-if="licenseData.ExpiresAt" class="text-sm text-gray-600">
                    <strong>{{ $t('ExpiresAt') }}:</strong> {{ formatDate(licenseData.ExpiresAt) }}
                  </p>
                </div>
              </div>
              
              <!-- Redirect Notice -->
              <p class="text-sm text-gray-500 mt-6">
                {{ $t('RedirectingToDashboard') }}
              </p>
            </div>

            <!-- Error State -->
            <div v-else-if="status === 'error'" class="error-state">
              <div class="error-icon mb-6">
                <i class="fas fa-exclamation-circle text-6xl text-danger"></i>
              </div>
              
              <h1 class="text-3xl font-bold text-danger mb-4 error-title">
                {{ $t('PaymentFinalizationFailed') }}
              </h1>
              
              <p class="text-lg text-gray-600 mb-6 error-description">
                {{ errorMessage || $t('AnErrorOccurredWhileFinalizing') }}
              </p>
              
              <!-- Action Buttons -->
              <div class="action-buttons">
                <vs-button 
                  color="primary" 
                  size="large"
                  @click="retry"
                  class="mr-4 action-btn">
                  <i class="fas fa-redo mr-2"></i>
                  {{ $t('Retry') }}
                </vs-button>
                
                <vs-button 
                  color="secondary" 
                  size="large"
                  @click="goToCatalog"
                  class="action-btn">
                  <i class="fas fa-arrow-left mr-2"></i>
                  {{ $t('BackToCatalog') }}
                </vs-button>
              </div>
              
              <!-- Support Info -->
              <div class="support-info mt-8">
                <p class="text-sm text-gray-500">
                  {{ $t('IfProblemPersists') }} 
                  <a href="mailto:support@tcvelik.com" class="text-primary underline">{{ $t('ContactSupport') }}</a>
                </p>
              </div>
            </div>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';
import accountsService from '@/accounts/accountsService.js';

export default {
  name: 'PaymentCallback',
  data() {
    return {
      status: 'initializing', // 'initializing', 'processing', 'success', 'error'
      errorMessage: null,
      licenseData: null
    };
  },
  mounted() {
    // Extract query params from URL
    const queryParams = this.$route.query;

    // Check if payment was successful
    if (queryParams.success !== 'true') {
      this.$router.push('/licensing/cancelled');
      return;
    }
    
    // Extract session_id from URL
    const sessionId = queryParams.session_id;
    
    if (!sessionId) {
      // No session_id - show error
      this.status = 'error';
      this.errorMessage = this.$t('PaymentSessionNotFound');
      return;
    }
    
    // Start finalization process
    this.finalizePayment(sessionId);
  },
  methods: {
    async finalizePayment(sessionId) {
      this.status = 'processing';
      
      try {
        // Get user email from localStorage (for verification, but backend will use session)
        const userInfo = accountsService.getUserObjectFromLocalStorageSafe();
        if (!userInfo || !userInfo.User || !userInfo.User.Email) {
          // User might not be logged in, but payment can still be processed
          // Backend will get email from Stripe session
        }
        
        // Call backend API to finalize payment
        const response = await TCVelikClient.PostRelative('/api/licensing/finalize-payment', {
          SessionId: sessionId
        });
        
        if (response.data.Success) {
          this.status = 'success';
          this.licenseData = response.data.LicenseData;
          
          // Show success notification
          this.$vs.notify({
            title: this.$t('LicenseActivated'),
            text: this.$t('YourLicenseHasBeenActivated'),
            color: 'success'
          });
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            this.goToDashboard();
          }, 3000);
        } else {
          throw new Error(response.data.Error || this.$t('FailedToFinalizePayment'));
        }
      } catch (error) {
        console.error('Error finalizing payment:', error);
        this.status = 'error';
        
        // Extract error message
        if (error.response && error.response.data && error.response.data.Error) {
          this.errorMessage = error.response.data.Error;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = this.$t('AnErrorOccurredWhileFinalizing');
        }
        
        // Show error notification
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.errorMessage,
          color: 'danger'
        });
      }
    },
    
    retry() {
      const queryParams = this.$route.query;
      const sessionId = queryParams.session_id;
      
      if (sessionId) {
        this.finalizePayment(sessionId);
      } else {
        this.$router.push('/licensing/catalog');
      }
    },
    
    goToDashboard() {
      this.$router.push('/pages/home');
    },
    
    goToCatalog() {
      this.$router.push('/licensing/catalog');
    },
    
    formatDate(dateString) {
      if (!dateString) return this.$t('NA');
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
/* Import Outfit font */
@import url('https://fonts.googleapis.com/css?family=Outfit:300,400,500,600,700');

.callback-page {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  align-items: center;
  font-family: 'Outfit', sans-serif;
}

.callback-card {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  border: none;
}

/* Loading State */
.loading-icon {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Success State */
.success-icon {
  animation: fadeInScale 0.6s ease-out;
}

.checkmark-container {
  display: inline-block;
  width: 80px;
  height: 80px;
}

.checkmark {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: block;
  stroke-width: 2;
  stroke: #10b981;
  stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #10b981;
  animation: fill 0.4s ease-in-out 0.4s forwards, scale 0.3s ease-in-out 0.9s both;
}

.checkmark-circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 2;
  stroke-miterlimit: 10;
  stroke: #10b981;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes fill {
  100% {
    box-shadow: inset 0px 0px 0px 30px #10b981;
  }
}

@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.success-title {
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  color: #10b981;
}

.success-description {
  font-family: 'Outfit', sans-serif;
  font-weight: 400;
  line-height: 1.6;
}

/* Error State */
.error-icon {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}

.error-title {
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  color: #ef4444;
}

.error-description {
  font-family: 'Outfit', sans-serif;
  font-weight: 400;
  line-height: 1.6;
}

/* License Info */
.license-info {
  max-width: 400px;
  margin: 0 auto;
}

.license-details {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.license-title {
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  color: #374151;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.action-btn {
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  min-width: 180px;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Support Info */
.support-info {
  max-width: 500px;
  margin: 0 auto;
  font-family: 'Outfit', sans-serif;
  font-weight: 400;
}

.support-info a {
  transition: color 0.2s ease;
}

.support-info a:hover {
  color: #1d4ed8;
}

@media (max-width: 768px) {
  .callback-page {
    padding: 1rem;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .action-btn {
    width: 100%;
    max-width: 300px;
  }
  
  .success-title,
  .error-title {
    font-size: 2rem;
  }
  
  .success-description,
  .error-description {
    font-size: 1rem;
  }
}
</style>

